/**
 * Global Artifact Upload / Link / Unlink
 *
 * Single write path for all artifact operations.
 * Uses the `global-artifacts` bucket with path: {shopId}/{artifactId}/{filename}
 */

import { supabase } from '@/integrations/supabase/client';
import {
  type ArtifactType,
  type StorageKind,
  type ArtifactSource,
  type LinkEntityType,
  type LinkRole,
  ARTIFACT_STATUS,
  STORAGE_KINDS,
  inferArtifactType,
} from './constants';
import { logArtifactEventToOcis } from '@/utils/ocisIngest';

const BUCKET = 'global-artifacts';

// ---------- Types ----------

export interface UploadArtifactParams {
  shopId: string;
  file: File;
  source: ArtifactSource;
  title?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  /** If provided, a link is created immediately */
  link?: {
    entityType: LinkEntityType;
    entityId: string;
    role: LinkRole;
  };
}

export interface CaptureLinkParams {
  shopId: string;
  externalUrl: string;
  title?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  link?: {
    entityType: LinkEntityType;
    entityId: string;
    role: LinkRole;
  };
}

export interface ArtifactRecord {
  id: string;
  shop_id: string;
  type: ArtifactType;
  storage_kind: StorageKind;
  storage_path: string | null;
  external_url: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  title: string | null;
  notes: string | null;
  status: string;
  source: string;
  created_at: string;
  created_by: string | null;
  metadata: Record<string, unknown>;
}

// ---------- Upload ----------

export async function uploadArtifact(params: UploadArtifactParams): Promise<ArtifactRecord> {
  const { shopId, file, source, title, notes, metadata, link } = params;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Pre-generate artifact ID so the storage path matches the DB row
  const artifactId = crypto.randomUUID();
  const ext = file.name.split('.').pop() || 'bin';
  const filename = `${crypto.randomUUID()}.${ext}`;
  // Programmatic path construction — guarantees {shopId}/{artifactId}/{filename}
  const storagePath = `${shopId}/${artifactId}/${filename}`;

  // 1. Upload to bucket
  const { error: uploadErr } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath, file, { contentType: file.type, upsert: false });

  if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`);

  // 2. Insert artifact row
  const artifactType = inferArtifactType(file.type);
  const { data: artifact, error: insertErr } = await supabase
    .from('artifacts')
    .insert({
      id: artifactId,
      shop_id: shopId,
      created_by: user.id,
      type: artifactType,
      storage_kind: STORAGE_KINDS.UPLOADED_BLOB,
      storage_path: storagePath,
      mime_type: file.type,
      size_bytes: file.size,
      source,
      title: title || file.name,
      notes,
      status: ARTIFACT_STATUS.ACTIVE,
      metadata: {
        ...metadata,
        original_filename: file.name,
      },
    } as any)
    .select()
    .single();

  if (insertErr) {
    // Cleanup uploaded file on insert failure
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw new Error(`Artifact record failed: ${insertErr.message}`);
  }

  // 3. Log OCIS artifact_created event (fire-and-forget)
  logArtifactEventToOcis({
    shopId,
    eventType: 'artifact_created',
    artifactId,
    storageKind: STORAGE_KINDS.UPLOADED_BLOB,
    artifactType,
    authorUserId: user.id,
    // If linking to a ticket, pass ticketId for thread attachment
    ticketId: link?.entityType === 'ticket' ? link.entityId : undefined,
  }).catch((err) => console.warn('[artifact] OCIS artifact_created failed:', err));

  // 4. Create link if requested
  if (link) {
    await createArtifactLink({
      artifactId,
      shopId,
      entityType: link.entityType,
      entityId: link.entityId,
      role: link.role,
    });
  }

  return artifact as unknown as ArtifactRecord;
}

// ---------- Link capture (external URL) ----------

export async function captureLink(params: CaptureLinkParams): Promise<ArtifactRecord> {
  const { shopId, externalUrl, title, notes, metadata, link } = params;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: artifact, error } = await supabase
    .from('artifacts')
    .insert({
      shop_id: shopId,
      created_by: user.id,
      type: 'link',
      storage_kind: STORAGE_KINDS.EXTERNAL_LINK,
      external_url: externalUrl,
      source: 'link_capture',
      title: title || externalUrl,
      notes,
      status: ARTIFACT_STATUS.ACTIVE,
      metadata: metadata || {},
    } as any)
    .select()
    .single();

  if (error) throw new Error(`Link capture failed: ${error.message}`);

  const capturedId = (artifact as any).id;

  // Log OCIS event (fire-and-forget)
  logArtifactEventToOcis({
    shopId,
    eventType: 'artifact_created',
    artifactId: capturedId,
    storageKind: STORAGE_KINDS.EXTERNAL_LINK,
    artifactType: 'link',
    authorUserId: user.id,
  }).catch((err) => console.warn('[artifact] OCIS link capture event failed:', err));

  if (link) {
    await createArtifactLink({
      artifactId: capturedId,
      shopId,
      entityType: link.entityType,
      entityId: link.entityId,
      role: link.role,
    });
  }

  return artifact as unknown as ArtifactRecord;
}

// ---------- Link / Unlink ----------

export async function createArtifactLink(params: {
  artifactId: string;
  shopId: string;
  entityType: LinkEntityType;
  entityId: string;
  role: LinkRole;
}): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  const { error } = await supabase
    .from('artifact_links')
    .insert({
      artifact_id: params.artifactId,
      shop_id: params.shopId,
      entity_type: params.entityType,
      entity_id: params.entityId,
      role: params.role,
      created_by: user?.id,
    } as any);

  if (error) throw new Error(`Link failed: ${error.message}`);

  // Log OCIS artifact_linked event (fire-and-forget)
  logArtifactEventToOcis({
    shopId: params.shopId,
    eventType: 'artifact_linked',
    artifactId: params.artifactId,
    storageKind: 'uploaded_blob', // generic; consumer doesn't need exact kind
    artifactType: 'other',
    linkedEntityType: params.entityType,
    linkedEntityId: params.entityId,
    role: params.role,
    authorUserId: user?.id,
    ticketId: params.entityType === 'ticket' ? params.entityId : undefined,
  }).catch((err) => console.warn('[artifact] OCIS artifact_linked failed:', err));
}

/**
 * Unlink an artifact from an entity.
 * If the artifact has zero remaining links, soft-delete it (status='deleted').
 * Never deletes the storage blob.
 */
export async function unlinkArtifact(linkId: string): Promise<void> {
  // Get artifact_id before deleting the link
  const { data: link, error: fetchErr } = await supabase
    .from('artifact_links')
    .select('artifact_id')
    .eq('id', linkId)
    .single();

  if (fetchErr) throw new Error(`Fetch link failed: ${fetchErr.message}`);

  const artifactId = (link as any).artifact_id;

  // Delete the link
  const { error: delErr } = await supabase
    .from('artifact_links')
    .delete()
    .eq('id', linkId);

  if (delErr) throw new Error(`Unlink failed: ${delErr.message}`);

  // Check if artifact is now orphaned
  const { count } = await supabase
    .from('artifact_links')
    .select('id', { count: 'exact', head: true })
    .eq('artifact_id', artifactId);

  if (count === 0) {
    // Soft-delete orphaned artifact
    await supabase
      .from('artifacts')
      .update({ status: ARTIFACT_STATUS.DELETED } as any)
      .eq('id', artifactId);
  }
}

// ---------- Signed URL ----------

export async function getArtifactSignedUrl(
  storagePath: string,
  expiresIn = 3600,
): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(storagePath, expiresIn);

  if (error) {
    console.error('Artifact signed URL error:', error);
    return null;
  }
  return data.signedUrl;
}

/**
 * Batched variant: returns a Map<storagePath, signedUrl> in a single round
 * trip. Use this when N artifacts need URLs at once (entity artifact list,
 * gallery views, etc.) to avoid the per-artifact N+1 storage call pattern.
 *
 * V-E-2 R151-HIGH-2.
 */
export async function getArtifactSignedUrls(
  storagePaths: string[],
  expiresIn = 3600,
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (storagePaths.length === 0) return result;

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(storagePaths, expiresIn);

  if (error) {
    console.error('Artifact signed URLs (batch) error:', error);
    return result;
  }
  for (const entry of data || []) {
    if (entry.signedUrl && entry.path) {
      result.set(entry.path, entry.signedUrl);
    }
  }
  return result;
}
